import React, { useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { Layers, ChevronLeft, ChevronRight, Eye, EyeOff, Navigation } from 'lucide-react';
import { Button } from '../ui/button';
import { useApp } from '../../context/AppContext';
import SpotMarker from './SpotMarker';
import UserLocationMarker from './UserLocationMarker';
import MapClickHandler from './MapClickHandler';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to dynamically update max zoom when layer changes
function MaxZoomHandler({ maxZoom, mapLayer }) {
  const map = useMap();
  
  useEffect(() => {
    if (map) {
      const currentZoom = map.getZoom();
      
      // First adjust zoom if needed (before setting maxZoom to avoid blank tiles)
      if (currentZoom > maxZoom) {
        // Smoothly zoom out to the new max
        map.setZoom(maxZoom, { animate: true, duration: 0.3 });
      }
      
      // Then update max zoom limit
      map.setMaxZoom(maxZoom);
    }
  }, [map, maxZoom, mapLayer]); // Also depend on mapLayer to trigger on layer change
  
  return null;
}

function MapView() {
  const { state, actions } = useApp();
  
  // Check if any modal is open to hide map controls
  const isModalOpen = state.showSpotDetails || state.showAddSpot || state.showLightbox;

  // Listen for custom event to center map on spot from gallery
  useEffect(() => {
    const handleCenterMapOnSpot = (event) => {
      const { latitude, longitude, zoom } = event.detail;
      if (state.mapRef && state.mapRef.setView) {
        state.mapRef.setView([latitude, longitude], zoom);
      }
    };

    window.addEventListener('centerMapOnSpot', handleCenterMapOnSpot);

    return () => {
      window.removeEventListener('centerMapOnSpot', handleCenterMapOnSpot);
    };
  }, [state.mapRef]);

  const getTileLayerUrl = () => {
    switch (state.mapLayer) {
      case 'satellite':
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
      case 'topo':
        return 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png';
      default:
        return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    }
  };

  // Get max zoom level for each tile provider
  const getTileLayerMaxZoom = () => {
    switch (state.mapLayer) {
      case 'satellite':
        return 18; // ArcGIS World Imagery max zoom
      case 'topo':
        return 17; // OpenTopoMap max zoom
      default:
        return 19; // OpenStreetMap max zoom
    }
  };

  const getTileLayerAttribution = () => {
    switch (state.mapLayer) {
      case 'satellite':
        return '&copy; <a href="https://www.esri.com/">Esri</a>';
      case 'topo':
        return '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors, <a href="https://opentopomap.org/">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)';
      default:
        return '&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors';
    }
  };

  const handleMapClick = () => {
    // Clear pin filter when clicking on empty map space
    actions.setSelectedSpotFromPin(null);
  };

  const spotsToShow = state.selectedSpotFromPin ? [state.selectedSpotFromPin] : state.spots;

  // Get current max zoom for the active tile layer
  const currentMaxZoom = getTileLayerMaxZoom();

  return (
    <div className="h-full relative">
      <MapContainer
        center={state.mapCenter}
        zoom={10}
        minZoom={2}
        maxZoom={19}
        className="h-full w-full"
        ref={actions.setMapRef}
        worldCopyJump={true}
        maxBounds={[[-85, -180], [85, 180]]}
        maxBoundsViscosity={1.0}
      >
        <MaxZoomHandler maxZoom={currentMaxZoom} mapLayer={state.mapLayer} />
        <TileLayer
          key={state.mapLayer}
          url={getTileLayerUrl()}
          attribution={getTileLayerAttribution()}
          maxZoom={currentMaxZoom}
          maxNativeZoom={currentMaxZoom}
        />
        
        <MapClickHandler onMapClick={handleMapClick} />
        
        {/* User Location Marker */}
        {state.showUserLocation && state.userLocation && (
          <UserLocationMarker
            position={state.userLocation}
            accuracy={state.userLocationAccuracy}
            showAccuracyCircle={true}
            autoCenter={state.centerOnUserLocation}
            map={state.mapRef}
          />
        )}
        
        {spotsToShow.map((spot, index) => (
          <SpotMarker
            key={spot.id}
            spot={spot}
            index={index}
            isSelected={state.selectedSpot?.id === spot.id}
          />
        ))}
      </MapContainer>

      {/* Layer Controls - Bottom Left - Hidden when modal is open */}
      <div className={`absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg p-3 shadow-lg border border-white/20 transition-opacity duration-200 ${isModalOpen ? 'opacity-0 pointer-events-none' : 'z-40'}`}>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Layers className="w-4 h-4 text-gray-600" />
            <p className="text-sm font-medium text-gray-700">Carte :</p>
          </div>
          <div className="grid grid-cols-1 gap-1 w-full min-w-[100px]">
            <button
              onClick={() => actions.setMapLayer('osm')}
              className={`text-left px-3 py-2 text-sm rounded ${
                state.mapLayer === 'osm' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Standard
            </button>
            <button
              onClick={() => actions.setMapLayer('satellite')}
              className={`text-left px-3 py-2 text-sm rounded ${
                state.mapLayer === 'satellite' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Satellite
            </button>
            <button
              onClick={() => actions.setMapLayer('topo')}
              className={`text-left px-3 py-2 text-sm rounded ${
                state.mapLayer === 'topo' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Relief
            </button>
          </div>
        </div>
      </div>

      {/* User Location Controls - Bottom Center - Hidden when modal is open */}
      <div className={`absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-white/20 transition-opacity duration-200 ${isModalOpen ? 'opacity-0 pointer-events-none' : 'z-40'}`}>
        <div className="flex items-center space-x-1">
          {state.userLocation ? (
            <>
              {/* Toggle User Location Visibility */}
              <Button
                variant={state.showUserLocation ? 'default' : 'outline'}
                size="sm"
                onClick={() => actions.setShowUserLocation(!state.showUserLocation)}
                className="flex items-center space-x-1 text-xs px-3"
                title={state.showUserLocation ? 'Masquer ma position' : 'Afficher ma position'}
              >
                {state.showUserLocation ? <Eye size={14} /> : <EyeOff size={14} />}
                <span className="hidden sm:inline">
                  {state.showUserLocation ? 'Ma position' : 'Position'}
                </span>
              </Button>

              {/* Center on User Location */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (state.mapRef && state.userLocation) {
                    state.mapRef.setView(state.userLocation, 14, { animate: true });
                  }
                }}
                className="flex items-center space-x-1 text-xs px-3"
                title="Centrer sur ma position"
              >
                <Navigation size={14} />
                <span className="hidden sm:inline">Centrer</span>
              </Button>

              {/* Position Accuracy Indicator */}
              {state.userLocationAccuracy && state.userLocationAccuracy < 100 && (
                <div className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                  ±{Math.round(state.userLocationAccuracy)}m
                </div>
              )}
            </>
          ) : (
            /* Request Location Button when no location available */
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      console.log('Manual geolocation success:', position.coords);
                      actions.setUserLocation([position.coords.latitude, position.coords.longitude]);
                      actions.setUserLocationAccuracy(position.coords.accuracy);
                    },
                    (error) => {
                      console.error('Manual geolocation error:', error);
                      alert('Impossible d\'obtenir votre position. Vérifiez les permissions de géolocalisation.');
                    },
                    {
                      enableHighAccuracy: true,
                      timeout: 10000,
                      maximumAge: 60000
                    }
                  );
                } else {
                  alert('La géolocalisation n\'est pas supportée par votre navigateur.');
                }
              }}
              className="flex items-center space-x-1 text-xs px-3"
              title="Activer la géolocalisation"
            >
              <Navigation size={14} />
              <span className="hidden sm:inline">Activer position</span>
            </Button>
          )}
        </div>
      </div>

      {/* View Control Button - Toggle between list and grid - Hidden when modal is open */}
      <div className={`absolute top-4 right-4 bg-white/95 backdrop-blur-sm rounded-lg p-2 shadow-lg border border-white/20 transition-opacity duration-200 ${isModalOpen ? 'opacity-0 pointer-events-none' : 'z-40'}`}>
        <Button
          variant="outline"
          size="sm"
          onClick={() => actions.setMapWidth(state.mapWidth === 66.67 ? 33.33 : 66.67)}
          className="p-2 h-8 w-8"
          title={state.mapWidth === 66.67 ? "Agrandir les spots (grille)" : "Réduire les spots (liste)"}
        >
          {state.mapWidth === 66.67 ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </Button>
      </div>

      {/* Clear Filter Button */}
      {state.selectedSpotFromPin && (
        <div className="absolute bottom-4 right-4">
          <button
            onClick={() => actions.setSelectedSpotFromPin(null)}
            className="bg-white hover:bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium shadow-sm transition-colors"
          >
            Voir tous les spots
          </button>
        </div>
      )}
    </div>
  );
}

export default MapView;